import { IThemeConfig, getConfig } from "../../../../_start/layout/core";

const defaultPageConfig = getConfig();

/**
 * Configuration for each step in the business creation workflow
 */
export type StepDifficulty = "easy" | "medium" | "hard";
export type StepRole = "strategist" | "executor";

export interface StepConfig {
  stepNumber: number;
  pageTitle: string;
  difficulty: StepDifficulty;
  estimatedTime: string;
  defaultRole: StepRole;
  showToolbar?: boolean;
  showSidebar?: boolean;
  sidebarContent?: "user" | "general" | "shop" | "gpt";
}

/**
 * Theme configuration for step dashboard pages
 */
export const createStepThemeConfig = (
  showToolbar = false,
  showSidebar = false,
  sidebarContent: "user" | "general" | "shop" | "gpt" = "gpt"
): Partial<IThemeConfig> => ({
  aside: {
    ...defaultPageConfig.aside,
    display: true,
    primaryDisplay: true,
    secondaryDisplay: false,
    toggle: true,
  },
  toolbar: {
    ...defaultPageConfig.toolbar,
    display: showToolbar,
  },
  sidebar: {
    ...defaultPageConfig.sidebar,
    display: showSidebar,
    content: sidebarContent,
  },
});

export type StepCategory =
  | "Market Opportunity"
  | "Customer Understanding"
  | "Value & Product"
  | "Business Model"
  | "Validation & Execution";

/**
 * All 24 step configurations — titles match the canonical 24-step framework in ARCHITECTURE.md
 * Category groupings: 1-5 Market Opportunity, 6-10 Customer Understanding,
 * 11-14 Value & Product, 15-19 Business Model, 20-24 Validation & Execution
 */
export const STEP_CONFIGS: StepConfig[] = [
  // Market Opportunity (1-5)
  { stepNumber: 1,  pageTitle: "Market Segmentation",                difficulty: "medium", estimatedTime: "~20 min", defaultRole: "strategist", showToolbar: true },
  { stepNumber: 2,  pageTitle: "Select a Beachhead Market",           difficulty: "medium", estimatedTime: "~25 min", defaultRole: "strategist" },
  { stepNumber: 3,  pageTitle: "Build an End User Profile",           difficulty: "easy",   estimatedTime: "~10 min", defaultRole: "executor" },
  { stepNumber: 4,  pageTitle: "Calculate TAM for Beachhead",         difficulty: "hard",   estimatedTime: "~45 min", defaultRole: "strategist" },
  { stepNumber: 5,  pageTitle: "Profile the Persona",                 difficulty: "hard",   estimatedTime: "~30 min", defaultRole: "executor" },
  // Customer Understanding (6-10)
  { stepNumber: 6,  pageTitle: "Full Life Cycle Use Case",            difficulty: "medium", estimatedTime: "~25 min", defaultRole: "executor" },
  { stepNumber: 7,  pageTitle: "High-Level Product Specification",    difficulty: "medium", estimatedTime: "~30 min", defaultRole: "strategist" },
  { stepNumber: 8,  pageTitle: "Quantify the Value Proposition",      difficulty: "hard",   estimatedTime: "~45 min", defaultRole: "strategist" },
  { stepNumber: 9,  pageTitle: "Identify Next 10 Customers",          difficulty: "medium", estimatedTime: "~20 min", defaultRole: "executor" },
  { stepNumber: 10, pageTitle: "Define Your Core",                    difficulty: "easy",   estimatedTime: "~15 min", defaultRole: "executor" },
  // Value & Product (11-14)
  { stepNumber: 11, pageTitle: "Chart Competitive Position",          difficulty: "medium", estimatedTime: "~30 min", defaultRole: "strategist" },
  { stepNumber: 12, pageTitle: "Determine Customer's DMU",            difficulty: "medium", estimatedTime: "~20 min", defaultRole: "executor" },
  { stepNumber: 13, pageTitle: "Map Process to Acquire Customer",     difficulty: "medium", estimatedTime: "~25 min", defaultRole: "executor" },
  { stepNumber: 14, pageTitle: "Calculate TAM for Follow-on",         difficulty: "hard",   estimatedTime: "~45 min", defaultRole: "strategist" },
  // Business Model (15-19)
  { stepNumber: 15, pageTitle: "Design a Business Model",             difficulty: "hard",   estimatedTime: "~60 min", defaultRole: "strategist" },
  { stepNumber: 16, pageTitle: "Set Your Pricing Framework",          difficulty: "medium", estimatedTime: "~30 min", defaultRole: "strategist" },
  { stepNumber: 17, pageTitle: "Calculate LTV",                       difficulty: "hard",   estimatedTime: "~45 min", defaultRole: "strategist" },
  { stepNumber: 18, pageTitle: "Map the Sales Process",               difficulty: "medium", estimatedTime: "~25 min", defaultRole: "executor" },
  { stepNumber: 19, pageTitle: "Calculate COCA",                      difficulty: "hard",   estimatedTime: "~45 min", defaultRole: "strategist" },
  // Validation & Execution (20-24)
  { stepNumber: 20, pageTitle: "Identify Key Assumptions",            difficulty: "easy",   estimatedTime: "~15 min", defaultRole: "executor" },
  { stepNumber: 21, pageTitle: "Test Key Assumptions",                difficulty: "hard",   estimatedTime: "~60 min", defaultRole: "executor" },
  { stepNumber: 22, pageTitle: "Define the MVP",                      difficulty: "medium", estimatedTime: "~30 min", defaultRole: "strategist" },
  { stepNumber: 23, pageTitle: "Show Dogs Will Eat the Food",         difficulty: "hard",   estimatedTime: "~60 min", defaultRole: "executor" },
  { stepNumber: 24, pageTitle: "Develop a Product Plan",              difficulty: "medium", estimatedTime: "~30 min", defaultRole: "strategist" },
];

/**
 * Get configuration for a specific step
 */
export const getStepConfig = (stepNumber: number): StepConfig | undefined => {
  return STEP_CONFIGS.find((config) => config.stepNumber === stepNumber);
};

/**
 * Get the default theme configuration
 */
export const getDefaultThemeConfig = () => defaultPageConfig;
